'use client';

import { withStoreCampaign } from '@/lib/store-attribution';
import { clenoirAppStoreUrl } from '@/lib/site';
import { useState } from 'react';

type BillingInterval = 'week' | 'year';

type BillingValues = {
  priceLabel: string;
  cadence: string;
  kicker?: string;
  badge?: string;
};

type PricingPlan = {
  name: 'Free' | 'Premium';
  audience: string;
  summary: string;
  note: string;
  features: string[];
  featured?: boolean;
  action: 'appstore' | 'checkout';
  monthly: BillingValues;
  yearly?: BillingValues;
};

const plans: PricingPlan[] = [
  {
    name: 'Free',
    audience: 'Free download',
    summary:
      'Install Cleanor from the App Store and clear the easy storage wins with Smart Cleanup.',
    note: 'Free on the App Store. Every scan runs on-device with no uploads.',
    features: [
      'Smart Cleanup for exact duplicates, blurry shots, and stale screenshots',
      'Storage analysis dashboard to see what is filling your iPhone',
      'Swipe Mode review for quick keep-or-remove decisions',
      'On-device processing keeps your media on the phone',
    ],
    action: 'appstore',
    monthly: {
      priceLabel: '$0',
      cadence: '',
    },
    yearly: {
      priceLabel: '$0',
      cadence: '',
    },
  },
  {
    name: 'Premium',
    audience: 'Full cleanup',
    summary:
      'Unlock Deep Analysis and the complete cleanup toolkit across photos, videos, contacts, and the encrypted Vault.',
    note: 'Everything in Free, plus Deep Analysis, unlimited cleanup, video compression, and the encrypted Vault. Subscribe inside the app.',
    features: [
      'Deep Analysis groups visually similar photos and videos, not just exact duplicates',
      'Unlimited cleanup with no per-scan limits',
      'Advanced video compression with space-savings estimates before export',
      'Encrypted Vault with biometric and PIN protection for sensitive media',
      'Contact merging, calendar cleanup, and the device health dashboard',
      'Priority support',
    ],
    featured: true,
    action: 'checkout',
    monthly: {
      priceLabel: '$7.99',
      cadence: '/ week',
      kicker: '3-day free trial, then $7.99/week',
      badge: 'Free trial',
    },
    yearly: {
      priceLabel: '$49.99',
      cadence: '/ year',
      kicker: '≈ $4.17/mo · billed yearly',
      badge: 'Save 88%',
    },
  },
];

export default function PricingPlansSection() {
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('year');

  return (
    <section className="overview-section pricing-plans-section">
      <div className="section-heading pricing-section-heading">
        <span className="eyebrow">Plans</span>
        <h2>Start free, go Premium for the full clean.</h2>
        <p>Switch between weekly and yearly billing. Yearly is the best value by far.</p>
      </div>

      <div className="pricing-billing-toggle-wrap">
        <div className="pricing-billing-toggle" aria-label="Billing interval">
          <button
            className={
              billingInterval === 'week'
                ? 'pricing-billing-option pricing-billing-option-active'
                : 'pricing-billing-option'
            }
            onClick={() => setBillingInterval('week')}
            type="button"
          >
            Weekly
            <span className="pricing-billing-save">3-day trial</span>
          </button>
          <button
            className={
              billingInterval === 'year'
                ? 'pricing-billing-option pricing-billing-option-active'
                : 'pricing-billing-option'
            }
            onClick={() => setBillingInterval('year')}
            type="button"
          >
            Yearly
            <span className="pricing-billing-save">Best value</span>
          </button>
        </div>
      </div>

      <div className="pricing-grid pricing-grid-duo">
        {plans.map((plan) => {
          const billing = billingInterval === 'year' ? (plan.yearly ?? plan.monthly) : plan.monthly;

          return (
            <article
              className={plan.featured ? 'pricing-card pricing-card-featured' : 'pricing-card'}
              key={plan.name}
            >
              {plan.featured ? <span className="pricing-card-ribbon">Recommended</span> : null}
              <div className="pricing-card-top">
                <div className="pricing-plan-copy">
                  <span className="pricing-plan-kicker">{plan.audience}</span>
                  <h3>{plan.name}</h3>
                </div>
                <span
                  className={
                    billing.badge
                      ? 'pricing-plan-badge'
                      : 'pricing-plan-badge pricing-plan-badge-placeholder'
                  }
                >
                  {billing.badge ?? 'Save'}
                </span>
              </div>

              <div className="pricing-price-block" key={`${plan.name}-${billingInterval}`}>
                <div className="pricing-price-row">
                  <strong>{billing.priceLabel}</strong>
                  {billing.cadence ? <span>{billing.cadence}</span> : null}
                </div>

                <p
                  className={
                    billing.kicker
                      ? 'pricing-billing-note'
                      : 'pricing-billing-note pricing-billing-note-placeholder'
                  }
                >
                  {billing.kicker ?? 'Free forever'}
                </p>
              </div>

              <div className="pricing-card-body">
                <p className="pricing-plan-summary">{plan.summary}</p>

                <ul className="pricing-feature-list">
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>

                <p className="pricing-plan-note">{plan.note}</p>
              </div>

              <div className="pricing-card-actions">
                <a
                  className={
                    plan.action === 'checkout' ? 'button' : 'button button-ghost button-ghost-light'
                  }
                  href={withStoreCampaign(clenoirAppStoreUrl, 'web-other')}
                  target="_blank"
                  rel="noreferrer"
                >
                  {plan.action === 'checkout' && billingInterval === 'week'
                    ? 'Start 3-day free trial'
                    : 'Download on the App Store'}
                </a>
              </div>
            </article>
          );
        })}
      </div>

      <p className="pricing-fineprint">
        Prices in USD, excluding local taxes. The 3-day free trial is available once, for new
        subscribers who have not used it before. Cancel anytime in your Apple account.
      </p>
    </section>
  );
}
